import "./Guess.css";
import { WORD_DATA } from "../../utils/constants";
import { useState, useEffect, useCallback } from "react";

function Guess() {
  const [currentWord, setCurrentWord] = useState(null);
  const [usedWords, setUsedWords] = useState([]);
  const [score, setScore] = useState(0);
  const [totalGuesses, setTotalGuesses] = useState(0);
  const [correctGuesses, setCorrectGuesses] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [userInput, setUserInput] = useState("");

  // Load game state from localStorage
  const loadGameState = useCallback(() => {
    try {
      const savedState = localStorage.getItem("wordGuessGameState");
      if (savedState) {
        const state = JSON.parse(savedState);
        setUsedWords(state.usedWords || []);
        setScore(state.score || 0);
        setTotalGuesses(state.totalGuesses || 0);
        setCorrectGuesses(state.correctGuesses || 0);

        // If there are used words but no current word, get a new one
        if (state.usedWords && state.usedWords.length > 0) {
          getNewWord(state.usedWords, state.score, false);
        } else {
          getNewWord([], 0, false);
        }
      } else {
        // No saved state, initialize fresh game
        getNewWord([], 0, false);
      }
    } catch (error) {
      console.error("Error loading game state:", error);
      // If loading fails, start fresh
      getNewWord([], 0, false);
    }
  }, []);

  // Save game state to localStorage
  const saveGameState = useCallback(() => {
    const gameState = {
      usedWords,
      score,
      totalGuesses,
      correctGuesses,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("wordGuessGameState", JSON.stringify(gameState));
  }, [usedWords, score, totalGuesses, correctGuesses]);

  // Clear game state from localStorage
  const clearGameState = useCallback(() => {
    localStorage.removeItem("wordGuessGameState");
  }, []);

  // Initialize the game
  const initGame = useCallback(() => {
    setUsedWords([]);
    setScore(0);
    setTotalGuesses(0);
    setCorrectGuesses(0);
    setHintsUsed(0);
    setMessage("");
    setMessageType("");
    setUserInput("");
    clearGameState();
    getNewWord([], 0, false);
  }, [clearGameState]);

  // Reset progress (clear all data and start fresh)
  const resetProgress = useCallback(() => {
    if (
      window.confirm(
        "Are you sure you want to reset all progress? This cannot be undone."
      )
    ) {
      initGame();
    }
  }, [initGame]);

  // Get a new random word
  const getNewWord = useCallback(
    (currentUsedWords = usedWords, currentScore = score, saveState = true) => {
      setMessage("");
      setMessageType("");
      setUserInput("");
      setHintsUsed(0);

      // If all words have been used, reset the used words list
      let newUsedWords = currentUsedWords;
      if (currentUsedWords.length >= WORD_DATA.length) {
        newUsedWords = [];
        setUsedWords([]);
        if (saveState) {
          setTimeout(() => saveGameState(), 0);
        }
      }

      // Find a word that hasn't been used yet
      const availableWords = WORD_DATA.filter(
        (word) => !newUsedWords.includes(word.word)
      );

      if (availableWords.length === 0) {
        // All words have been used
        setMessage("Congratulations! You've guessed all the words!");
        setMessageType("win");
        setCurrentWord({
          clue: "Game Complete!",
          category: "general vocabulary",
          word: "",
        });
        if (saveState) {
          setTimeout(() => saveGameState(), 0);
        }
        return;
      }

      // Select a random word
      const newWord =
        availableWords[Math.floor(Math.random() * availableWords.length)];
      const updatedUsedWords = [...newUsedWords, newWord.word];

      setCurrentWord(newWord);
      setUsedWords(updatedUsedWords);

      // Save state after a brief delay to ensure state updates
      if (saveState) {
        setTimeout(() => saveGameState(), 0);
      }
    },
    [usedWords, score, saveGameState]
  );

  // Check the user's guess
  const checkGuess = () => {
    if (!userInput.trim()) {
      setMessage("Please enter a guess");
      setMessageType("lose");
      return;
    }

    const newTotalGuesses = totalGuesses + 1;
    setTotalGuesses(newTotalGuesses);

    if (userInput.trim().toLowerCase() === currentWord.word.toLowerCase()) {
      // Correct guess
      const points = 10 - hintsUsed;
      const newScore = score + points;
      const newCorrectGuesses = correctGuesses + 1;

      setScore(newScore);
      setCorrectGuesses(newCorrectGuesses);
      setMessage(`Correct! The word was "${currentWord.word}"`);
      setMessageType("win");

      // Wait a moment and then get a new word
      setTimeout(() => getNewWord(usedWords, newScore), 1500);
    } else {
      // Incorrect guess
      setMessage("Incorrect. Try again!");
      setMessageType("lose");
      setUserInput("");
      // Save state for incorrect guess count
      setTimeout(() => saveGameState(), 0);
    }
  };

  // Provide an additional hint
  const giveHint = () => {
    if (!currentWord || hintsUsed >= 3) return;

    const newHintsUsed = hintsUsed + 1;
    setHintsUsed(newHintsUsed);

    // Different types of hints based on how many have been used
    switch (newHintsUsed) {
      case 1:
        // Show first letter (skip spaces)
        const firstLetter = currentWord.word.replace(/\s/g, "")[0];
        setMessage(`Hint: The word starts with "${firstLetter}"`);
        setMessageType("");
        break;
      case 2:
        // Show last letter (skip spaces)
        const lettersWithoutSpaces = currentWord.word.replace(/\s/g, "");
        const lastLetter =
          lettersWithoutSpaces[lettersWithoutSpaces.length - 1];
        setMessage(`Hint: The word ends with "${lastLetter}"`);
        setMessageType("");
        break;
      case 3:
        // Show a random non-space letter from the word
        const nonSpaceLetters = currentWord.word.replace(/\s/g, "");
        const randomIndex = Math.floor(Math.random() * nonSpaceLetters.length);
        const randomLetter = nonSpaceLetters[randomIndex];

        // Find the actual position in the original string (counting only non-space characters)
        let position = 0;
        let found = false;
        for (let i = 0; i < currentWord.word.length; i++) {
          if (currentWord.word[i] !== " ") {
            if (currentWord.word[i] === randomLetter && !found) {
              position = i + 1;
              found = true;
            }
          }
        }

        setMessage(
          `Hint: The ${position}${getOrdinalSuffix(
            position
          )} letter is "${randomLetter}"`
        );
        setMessageType("");
        break;
    }
  };

  // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
  const getOrdinalSuffix = (i) => {
    const j = i % 10;
    const k = i % 100;

    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  };

  // Count words in a string (for multi-word answers)
  const countWords = (str) => {
    return str
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  // Calculate word length without spaces
  const getWordLengthWithoutSpaces = (str) => {
    return str.replace(/\s/g, "").length;
  };

  // Skip the current word
  const skipWord = () => {
    setMessage(`Skipped! The word was "${currentWord.word}"`);
    setMessageType("lose");
    setTimeout(() => getNewWord(usedWords, score), 1500);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      checkGuess();
    }
  };

  // Initialize game on component mount
  useEffect(() => {
    loadGameState();
  }, [loadGameState]);

  // Save game state when relevant states change
  useEffect(() => {
    // Don't save on initial load
    if (usedWords.length > 0 || score > 0 || totalGuesses > 0) {
      saveGameState();
    }
  }, [usedWords, score, totalGuesses, correctGuesses, saveGameState]);

  return (
    <div className='Guess'>
      <div className='container'>
        <h1>Word Guessing Game - A1/A2 Level</h1>

        <div className='instructions'>
          <h2>Instructions</h2>
          <p>
            Read the simple explanation below and write the correct word from
            the word list. The words are mixed from different categories.
            <br />
            <strong>Your progress is automatically saved!</strong>
          </p>
        </div>

        <div className='game-area'>
          <div className='game-info'>
            <div>
              Score: <span id='score'>{score}</span>
            </div>
            <div>
              Words Remaining:{" "}
              <span id='remaining'>{WORD_DATA.length - usedWords.length}</span>
            </div>
            <div>
              Correct: <span id='correct'>{correctGuesses}</span>/
              <span id='total'>{totalGuesses}</span>
            </div>
          </div>

          <div className='clue-container'>
            <div className='clue' id='clue'>
              {currentWord ? currentWord.clue : "Loading game..."}
            </div>
            {currentWord && (
              <div className='word-info'>
                <div className='word-length' id='word-length'>
                  Answer length:{" "}
                  {countWords(currentWord.word) > 1 ? (
                    <span> {countWords(currentWord.word)} words, </span>
                  ) : (
                    <span>1 word, </span>
                  )}
                  {getWordLengthWithoutSpaces(currentWord.word)} letters
                  {/* {" "}
                  {countWords(currentWord.word) > 1 && "(excluding spaces)"} */}
                </div>
              </div>
            )}
          </div>

          <div
            className='hint'
            id='hint'
            style={{ display: currentWord ? "block" : "none" }}
          >
            Hint: The word is about{" "}
            <span id='category'>
              {currentWord
                ? currentWord.category.toLowerCase()
                : "general vocabulary"}
            </span>
          </div>

          <div className='input-area'>
            <input
              type='text'
              id='guess-input'
              placeholder='Type your guess here...'
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              autoFocus
            />
            <button id='submit-btn' onClick={checkGuess}>
              Submit Guess
            </button>
          </div>

          <div className={`message ${messageType}`} id='message'>
            {message}
          </div>

          <div className='controls'>
            <button
              id='hint-btn'
              onClick={giveHint}
              disabled={!currentWord || hintsUsed >= 3}
            >
              Get Another Hint ({3 - hintsUsed} left)
            </button>
            <button
              id='new-word-btn'
              onClick={() => getNewWord(usedWords, score)}
            >
              New Word
            </button>
            <button id='skip-btn' onClick={skipWord} disabled={!currentWord}>
              Skip Word
            </button>
            {/* <button id='reset-btn' onClick={initGame}>
              Reset Game
            </button> */}
            <button
              id='reset-progress-btn'
              onClick={resetProgress}
              className='reset-progress'
            >
              Reset All Progress
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Guess;
